import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { resolveRelationModel, type ReferenceSource } from '../../relation/relation-registry';

/**
 * The two reference reads behind a many2one field (#1804 batch C).
 *
 * Both hooks are deliberately thin: they own the request GATE and the id → label
 * projection, and nothing else. The gate is the interesting part — an empty
 * reference set and an empty search box are states whose answer is known before
 * a request is sent, and `getMany` would happily be called with no ids.
 */

const asString = (value: unknown): string | null =>
  typeof value === 'string' && value !== '' ? value : null;

/**
 * The registered target, resolved once per model name. The registry is filled
 * by the composition root before the tree mounts, so a memo on the name is
 * enough — there is no "registered later" state to react to.
 */
export function useRelationTarget(model: string): ReferenceSource | null {
  return useMemo(() => resolveRelationModel(model), [model]);
}

export interface ReferenceCandidate {
  id: string;
  label: string;
}

function projectRows(source: ReferenceSource, rows: readonly unknown[]): ReferenceCandidate[] {
  const candidates: ReferenceCandidate[] = [];
  for (const row of rows) {
    const record = row as Record<string, unknown>;
    const id = asString(record[source.idField]);
    const label = asString(record[source.recName]);
    if (id !== null) candidates.push({ id, label: label ?? id });
  }
  return candidates;
}

/**
 * Resolve reference ids to their display names through `source.getMany`.
 *
 * SHORT-CIRCUIT: with no ids the query never runs. #1793's reviewers flagged
 * this on the endpoint side — the caller must not ask for an empty id list —
 * and it is enforced here, once, rather than at every call site.
 */
export function useReferenceLabels(
  model: string,
  ids: readonly string[],
): {
  source: ReferenceSource | null;
  labels: ReadonlyMap<string, string>;
  isPending: boolean;
} {
  const source = useRelationTarget(model);
  // Sorted + joined so ['a','b'] and ['b','a'] share one cache entry, and so
  // the key is a PRIMITIVE: an array literal is a fresh identity every render
  // and would hand out a new cache entry each time. The separator is ASCII
  // Unit Separator, which cannot occur in an id, so ['ab','c'] and ['a','bc']
  // cannot digest alike. It is a cache key ONLY — the request itself is built
  // from `ids` and never parsed back out of this string.
  const key = useMemo(() => [...ids].sort().join(''), [ids]);
  const enabled = source !== null && ids.length > 0;

  const query = useQuery({
    queryKey: ['resource-kit', 'reference', model, key],
    queryFn: async () => {
      // Unreachable while disabled; narrowed rather than asserted.
      if (!source) return new Map<string, string>();
      const rows = await source.getMany(ids);
      // An id with no live row is skipped by the endpoint (#1793), so a
      // missing entry is a fact about the data, not a failure.
      return new Map(projectRows(source, rows).map((c) => [c.id, c.label]));
    },
    enabled,
    staleTime: 5 * 60_000,
  });

  return {
    source,
    labels: query.data ?? new Map<string, string>(),
    isPending: enabled && query.isPending,
  };
}

/**
 * Candidates for the edit-mode select, through `source.searchReference`.
 *
 * SHORT-CIRCUIT: a blank query never runs. The alternative — listing the first
 * page of the target model — is a different feature (a picker), and doing it
 * implicitly would fire a request for every field that merely enters edit mode.
 */
export function useReferenceSearch(
  model: string,
  query: string,
): {
  candidates: readonly ReferenceCandidate[];
  isPending: boolean;
} {
  const source = useRelationTarget(model);
  const needle = query.trim();
  const enabled = source !== null && needle !== '';

  const result = useQuery({
    queryKey: ['resource-kit', 'reference-search', model, needle],
    queryFn: async () => {
      if (!source) return [] as ReferenceCandidate[];
      return projectRows(source, (await source.searchReference(needle)).items);
    },
    enabled,
    staleTime: 30_000,
  });

  return {
    candidates: result.data ?? [],
    isPending: enabled && result.isPending,
  };
}
